import {
  CUSTOM_SORT_ORDERS,
  PS_APP_PROG_ONBOARDING_FEATURE_KEY,
  PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY,
  PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';
import { ONBOARDING_CONTENT } from 'providers/ProgressiveOnboarding/constants/workflows';
import { useProgressiveOnboarding } from 'providers/ProgressiveOnboarding/ProgressiveOnboardingProvider';

import type {
  Guide,
  OnboardingState,
  OnboardingStep,
  Step,
  StepState,
} from 'providers/ProgressiveOnboarding/types';

/**
 * Maps progressive onboarding feature keys based on is_CS_Led status
 */
export const getMappedFeatureKey = (
  featureKey: string,
  isCSLed: boolean,
): string => {
  if (featureKey === PS_APP_PROG_ONBOARDING_FEATURE_KEY) {
    return isCSLed
      ? PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY
      : PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY;
  }
  return featureKey;
};

/**
 * Determines the state of a given onboarding step based on its completion status,
 * dependencies, and the completion status of those dependencies.
 */
export const getStepState = (
  step: OnboardingStep,
  allSteps: OnboardingStep[],
): StepState => {
  if (step.completedAt) return 'complete';

  const hasBlockingDependencies = step.dependsOn.some((dep) => {
    const dependentStep = allSteps.find((s) => s.stepKey === dep);
    return !dependentStep?.completedAt;
  });

  return hasBlockingDependencies ? 'blocked' : 'incomplete';
};

/** Lookup the copy for a step by its key, and merge with the step's state */
export const createStep = (
  currentStep: OnboardingStep,
  onboardingState: OnboardingState,
) => {
  const content =
    ONBOARDING_CONTENT[onboardingState.featureKey]?.steps[currentStep.stepKey];

  if (!content) {
    console.warn(`Missing content for step key: ${currentStep.stepKey}`);
    return null;
  }

  return {
    ...content,
    state: getStepState(currentStep, onboardingState.states),
  } as Step;
};

/** Create the steps for a guide */
export const createGuide = (onboardingState: OnboardingState) => {
  const customOrderMap = CUSTOM_SORT_ORDERS[onboardingState.featureKey];

  const steps = onboardingState.states
    .sort((a, b) => {
      if (customOrderMap) {
        const aOrder = customOrderMap[a.stepKey] ?? 999;
        const bOrder = customOrderMap[b.stepKey] ?? 999;
        return aOrder - bOrder;
      }
      return a.stepOrder - b.stepOrder;
    })
    .map((step) => createStep(step, onboardingState))
    .filter(Boolean) as Step[];

  const content = ONBOARDING_CONTENT[onboardingState.featureKey];

  return {
    ...content,
    steps,
  } as Guide;
};

export const incompleteGuides = (onboardingState: OnboardingState) =>
  onboardingState.states.some((step) => step.completedAt === null);

/**
 * Custom hook that provides a function to mark a specific step as completed for a given guide
 */
export const useMarkStepCompletedIfAvailable = () => {
  const { availableGuides, markStepCompleted } = useProgressiveOnboarding();

  return ({ guideKey, stepKey }: { guideKey: string; stepKey: string }) => {
    const guide = availableGuides.find((g) => g.key === guideKey);

    if (!guide) return;

    const step = guide.steps.find((s) => s.key === stepKey);

    if (!step || step.state === 'complete') return;

    // Map touchless/managed feature keys back to the base feature key for the API
    const mappedGuideKey =
      guide.key === PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY ||
      guide.key === PS_APP_PROG_ONBOARDING_MANAGED_FEATURE_KEY
        ? PS_APP_PROG_ONBOARDING_FEATURE_KEY
        : guide.key;

    markStepCompleted({
      guideKey: mappedGuideKey,
      stepKey,
    });
  };
};
